import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

/* Util */
import { convertToViewFormatFromDateTime } from 'util/DateTimeUtil';

/* Component */
import TimeCalendar from 'components/TimeCalendar';
import Required from 'util/Required';

/* API */
import ReservationTypeService from 'api/ReservationTypeService';
import { ReservationTypes } from 'api/ReservationTypeService';
import ReservationService from 'api/ReservationService';
import {
  ReservationTypeResponseDto as ReservationType,
  CreateReservationDto,
} from 'api/generated/openapi';
import { ReservationSchedule } from 'api/ReservationScheduleService';

/* Layout */
import './FormComponent.scss';
import Form from 'react-bootstrap/Form';
import CenteredRow from 'layout/CenteredRow';
import InfoRow from 'layout/InfoRow';
import PrimaryButton from 'util/PrimaryButton';
import SecondaryButton from 'util/SecondaryButton';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import LoadingSpinner from 'util/LoadingSpinner';

/* Asset */
import checkCircle from 'assets/image/checkCircle.svg';
import folder from 'assets/image/folder.svg';
import calendar from 'assets/image/calendar.svg';
import human from 'assets/image/human.svg';
import cross from 'assets/image/cross.svg';

/* Types */
import { ReservationMode } from 'types/ReservationMode';

type Step = '1' | '2' | 'confirm' | 'registration';
type ReservationScheduleList = (ReservationSchedule & {
  minute: number;
})[];

const FormComponent: React.FC = () => {
  /* 共有state */
  const { tenpoCode } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [currentStep, setCurrentStep] = useState<Step>();

  /* 予約種別state */
  const [reservationTypes, setReservationTypes] = useState<ReservationTypes>([]);
  const [reservationTypeIndex, setReservationTypeIndex] = useState<number>(0);

  /* 予約カレンダーstate */
  const [reservationSchedule, setReservationSchedule] = useState<ReservationSchedule>();
  const [reservationScheduleList, setReservationScheduleList] = useState<ReservationScheduleList>(
    [],
  );
  const [reservationDateTime, setReservationDateTime] = useState<string>();
  const [reservationMode, setReservationMode] = useState<number>();
  const isRequestReservation = useMemo(
    () => reservationMode === ReservationMode.REQUEST,
    [reservationMode],
  );

  /* 入力値state */
  const reservationTypeMinutes =
    reservationTypes[reservationTypeIndex]?.reservationTypeMinutes || 0;
  const reservationTypeName = reservationTypes[reservationTypeIndex]?.reservationTypeName || '';
  const formattedReservationDateTime = reservationDateTime
    ? convertToViewFormatFromDateTime(reservationDateTime)
    : '';
  const [reservationCustomerName, setReservationCustomerNameName] = useState<string>();
  const [reservationCustomerTel, setReservationCustomerTel] = useState<string>();

  /* エラーstate */
  const [isReservationCustomerNameInvalid, setIsReservationCustomerNameInvalid] = useState(false);
  const [isReservationCustomerTelInvalid, setIsReservationCustomerTelInvalid] = useState(false);
  const [isReservationCustomerTelLengthInvalid, setIsReservationCustomerTelLengthInvalid] =
    useState(false);

  /* 表示制御state */
  const [isSkip, setIsSkip] = useState(true);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);
  const [hasSentSms, setHasSentSms] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    async function fetchData() {
      try {
        const reservationTypeResult = await ReservationTypeService.get({ postId: tenpoCode ?? '' });
        setReservationTypes(reservationTypeResult);
        await getReservationScheduleList(0, reservationTypeResult);

        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
      }
    }
    fetchData();
  }, []);

  useEffect(() => {
    const schedule = reservationScheduleList.find(
      (schedule) => schedule.minute === reservationTypeMinutes,
    );
    if (!!schedule) {
      setReservationSchedule(schedule);
    }
  }, [reservationTypeMinutes, reservationScheduleList]);

  useEffect(() => {
    if (
      !!reservationCustomerName?.trim() &&
      !isReservationCustomerNameInvalid &&
      !!reservationCustomerTel &&
      !isReservationCustomerTelInvalid &&
      !isReservationCustomerTelLengthInvalid
    ) {
      setIsSubmitDisabled(false);
    } else {
      setIsSubmitDisabled(true);
    }
  }, [reservationCustomerName, reservationCustomerTel, isReservationCustomerTelInvalid]);

  const handleChangeMinutes = (event: React.ChangeEvent<HTMLInputElement>) => {
    setReservationTypeIndex(Number(event.target.value));
  };

  const handleClickCell = (reservationDateTime: string, reservationStatusCode: number) => {
    setReservationDateTime(reservationDateTime);
    setReservationMode(reservationStatusCode);
    changeNextStep();
  };

  const handleChangeName = (event: React.ChangeEvent<HTMLInputElement>) => {
    const reservationCustomerName = event.target.value;
    if (!reservationCustomerName) {
      setIsReservationCustomerNameInvalid(false);
      setReservationCustomerNameName('');
      return;
    }

    if (/[0-9!-/:@[-`{-~]/.test(reservationCustomerName)) {
      setIsReservationCustomerNameInvalid(true);
    } else {
      setIsReservationCustomerNameInvalid(false);
    }

    setReservationCustomerNameName(reservationCustomerName);
  };

  const handleChangeTel = (event: React.ChangeEvent<HTMLInputElement>) => {
    const reservationCustomerTel = event.target.value;
    if (!reservationCustomerTel) {
      setIsReservationCustomerTelInvalid(false);
      setIsReservationCustomerTelLengthInvalid(false);
      setReservationCustomerTel('');
      return;
    }

    if (!/^\d*$/.test(reservationCustomerTel)) {
      setIsReservationCustomerTelInvalid(true);
    } else {
      setIsReservationCustomerTelInvalid(false);
    }

    if (!/^\d{10,11}$/.test(reservationCustomerTel)) {
      setIsReservationCustomerTelLengthInvalid(true);
    } else {
      setIsReservationCustomerTelLengthInvalid(false);
    }

    setReservationCustomerTel(reservationCustomerTel);
  };

  const getReservationScheduleList = async (
    week: number,
    reservationTypeList: ReservationTypes = reservationTypes,
  ) => {
    // 速度改善のために並列でリクエスト
    const scheduleRequests = reservationTypeList.map((reservationType) => {
      const minute = reservationType.reservationTypeMinutes;
      return ReservationService.getReservationList({
        tenpoCode: tenpoCode ?? '',
        reservationTypeMinutes: minute,
        week: week,
      }).then((result) => {
        return { minute, ...result };
      });
    });

    const resultList = await Promise.all(scheduleRequests);
    updateSchedule(resultList);
  };

  const updateSchedule = (scheduleList: ReservationScheduleList) => {
    if (reservationScheduleList.length === 0) {
      setReservationScheduleList(scheduleList);
    } else {
      scheduleList.map((s) => {
        setReservationScheduleList((prev) =>
          prev.map((prev) =>
            prev.minute === s.minute
              ? {
                  ...prev,
                  schedule: {
                    ...prev.schedule,
                    ...s.schedule,
                  },
                }
              : prev,
          ),
        );
      });
    }
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    console.log(
      tenpoCode,
      reservationTypeName,
      reservationTypeMinutes,
      reservationDateTime,
      reservationCustomerName,
      reservationCustomerTel,
    );
    if (isSkip) {
      createReservation();
    } else {
      changeNextStep();
    }
  };

  const handleConfirmSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    createReservation();
  };

  const createReservation = async () => {
    setIsSending(true);

    const createReservationDto: CreateReservationDto = {
      tenpoCode: tenpoCode || '',
      reservationDateTime: reservationDateTime || '',
      reservationCustomerName: reservationCustomerName || '',
      reservationCustomerTel: reservationCustomerTel || '',
      reservationTypeName: reservationTypeName,
      reservationTypeMinutes: reservationTypeMinutes.toString(),
      screenFlag: 2,
      requestStatusCode: isRequestReservation ? 1 : 0,
      message_type: isRequestReservation ? 'preferred' : 'tentative',
    };

    try {
      await ReservationService.createReservation({ createReservationDto });
      setHasSentSms(true);
    } catch (error) {
      setHasSentSms(false);
    } finally {
      setIsSending(false);
      changeNextStep();
    }
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as any });
  }, [currentStep]);

  const changePreviousStep = () => {
    if (currentStep === 'registration') {
      return setCurrentStep('1');
    }
    if (currentStep === 'confirm') {
      return setCurrentStep('2');
    }
    return setCurrentStep('1');
  };

  const changeNextStep = () => {
    if (currentStep === '2') {
      return setCurrentStep(isSkip ? 'registration' : 'confirm');
    }
    if (currentStep === 'confirm') {
      return setCurrentStep('registration');
    }
    return setCurrentStep('2');
  };

  const handleClickPreviousStep = () => {
    changePreviousStep();
  };

  const createReservationTypeLabel = (reservationType: ReservationType) => {
    return (
      <>
        {reservationType.reservationTypeName}({reservationType.reservationTypeMinutes}分)
      </>
    );
  };

  const handleChangeStep = () => {
    setIsSkip(!isSkip);
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }
  if (!reservationTypes && reservationScheduleList.length === 0) {
    navigate('/404');
  }

  switch (currentStep) {
    default:
      return (
        <>
          <CenteredRow>
            <p className="">
              <img src={folder} alt="" className="icon" />
              ご要望
            </p>
            <Form.Group className="ps-4">
              {reservationTypes.map((reservationType, index) => (
                <Form.Check
                  required
                  key={index}
                  type="radio"
                  name="radioGroup"
                  defaultChecked={index === reservationTypeIndex}
                  onChange={handleChangeMinutes}
                  value={index}
                  label={createReservationTypeLabel(reservationType)}
                  id={`radio-${index}`}
                />
              ))}
            </Form.Group>
          </CenteredRow>
          <CenteredRow>
            <p className="">
              <img src={calendar} alt="" className="icon" />
              予約日時
            </p>
            <Form.Group className="ps-sm-4 pt-4">
              <TimeCalendar
                handleClickCell={handleClickCell}
                getReservationScheduleList={getReservationScheduleList}
                reservationSchedule={reservationSchedule}
              />
            </Form.Group>
          </CenteredRow>
        </>
      );
    case '2':
      return (
        <>
          <CenteredRow>
            <p className="">
              <img src={folder} alt="" className="icon" />
              入力情報
            </p>
          </CenteredRow>
          <Row className="justify-content-center">
            <InfoRow childrenLeft={<p>ご要望</p>} childrenRight={<p>{reservationTypeName}</p>} />
            <InfoRow
              childrenLeft={<p>ご来店時間</p>}
              childrenRight={<p> {formattedReservationDateTime}</p>}
            />
            <InfoRow
              childrenLeft={<p>所要時間</p>}
              childrenRight={<p>{reservationTypeMinutes} 分</p>}
            />
            {isRequestReservation && (
              <Row className={`justify-content-center`}>
                <Col sm={6} xs={10}>
                  <div className="text-danger">
                    ※ご予約は「リクエスト予約」として受付いたします。
                    後ほど、担当者よりお電話またはSMSでご連絡いたしますので、ご了承ください。
                  </div>
                </Col>
              </Row>
            )}
          </Row>
          <Form onSubmit={handleSubmit}>
            <CenteredRow>
              <p className="">
                <img src={human} alt="" className="icon" />
                お客様情報
              </p>
              <Form.Group className="ps-4 mb-4">
                <Form.Label htmlFor="">
                  お名前
                  <Required />
                </Form.Label>
                <Form.Control
                  className="w-100"
                  type="text"
                  name="name"
                  onChange={handleChangeName}
                  value={reservationCustomerName}
                  isInvalid={isReservationCustomerNameInvalid}
                  maxLength={30}
                />
                <Form.Control.Feedback type="invalid">
                  {isReservationCustomerNameInvalid && (
                    <p>お名前には記号や数字を含めないでください。</p>
                  )}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group className="ps-4">
                <Form.Label htmlFor="">
                  携帯番号 <Required />
                </Form.Label>
                <Form.Control
                  className="w-100"
                  name="tel"
                  type="tel"
                  onChange={handleChangeTel}
                  isInvalid={
                    isReservationCustomerTelInvalid || isReservationCustomerTelLengthInvalid
                  }
                  maxLength={11}
                  value={reservationCustomerTel}
                />
                <Form.Control.Feedback type="invalid">
                  {isReservationCustomerTelInvalid && (
                    <p>半角数字のハイフンなしで入力してください。</p>
                  )}
                  {isReservationCustomerTelLengthInvalid && (
                    <p>電話番号は10桁または11桁の半角数字で入力してください。</p>
                  )}
                </Form.Control.Feedback>
              </Form.Group>
            </CenteredRow>

            <CenteredRow className="text-center pb-0">
              <a
                className="btn btn-secondary w-50 text-white"
                href="https://www.token.co.jp/privacy/"
                target="_blank"
              >
                個人情報保護に関する <br />
                基本方針について
              </a>
              <Row className={`justify-content-center`}>
                <div style={{ width: '80%' }}>
                  <p className="mt-3 mb-0 ms-3 text-start">
                    今回、お送り頂いたお客様の個人情報につきましては、
                    お問合せに対する回答のご連絡以外で利用することはございません。
                  </p>
                </div>
              </Row>
            </CenteredRow>
            <Row className="justify-content-md-center">
              <Col sm={6} className="d-flex flex-column align-items-center py-4">
                <Form.Check
                  type="checkbox"
                  onChange={handleChangeStep}
                  label="確認画面をスキップする"
                  className="mt-3"
                  id={'skip'}
                  checked={isSkip}
                ></Form.Check>
                <PrimaryButton
                  label={isRequestReservation ? '予約をリクエストする' : '来店予約をする'}
                  type="submit"
                  className="w-50 mt-3 d-block"
                  disabled={isSubmitDisabled || isSending}
                />
                <SecondaryButton
                  label="戻る"
                  type="button"
                  className="w-25 mt-3 d-block"
                  onClick={handleClickPreviousStep}
                />
              </Col>
            </Row>
          </Form>
        </>
      );
    case 'confirm':
      return (
        <>
          <CenteredRow>
            <p className="">
              <img src={folder} alt="" className="icon" />
              入力情報
            </p>
          </CenteredRow>
          <Row className="justify-content-center">
            <InfoRow childrenLeft={<p>ご要望</p>} childrenRight={<p>{reservationTypeName}</p>} />
            <InfoRow
              childrenLeft={<p>ご来店時間</p>}
              childrenRight={<p> {formattedReservationDateTime}</p>}
            />
            <InfoRow
              childrenLeft={<p>所要時間</p>}
              childrenRight={<p>{reservationTypeMinutes} 分</p>}
            />
            <InfoRow
              childrenLeft={<p> お名前</p>}
              childrenRight={<p> {reservationCustomerName}</p>}
            />
            <InfoRow
              childrenLeft={<p> 電話番号</p>}
              childrenRight={<p> {reservationCustomerTel}</p>}
            />
            {isRequestReservation && (
              <Row className={`justify-content-center`}>
                <Col sm={6} xs={10}>
                  <div className="text-danger">
                    ※ご予約は「リクエスト予約」として受付いたします。
                    後ほど、担当者よりお電話またはSMSでご連絡いたしますので、ご了承ください。
                  </div>
                </Col>
              </Row>
            )}
          </Row>
          <Form onSubmit={handleConfirmSubmit}>
            <Row className="justify-content-md-center">
              <Col sm={6} className="d-flex flex-column align-items-center py-4">
                <PrimaryButton
                  label={isRequestReservation ? '予約をリクエストする' : '確認'}
                  type="submit"
                  className="w-50"
                  disabled={isSending}
                />
                <SecondaryButton
                  label="戻る"
                  type="button"
                  className="w-25 mt-3"
                  onClick={handleClickPreviousStep}
                />
              </Col>
            </Row>
          </Form>
        </>
      );
    case 'registration':
      return (
        <div className="container border text-center w-75 mt-4">
          <div className="p-3">
            <img src={hasSentSms ? checkCircle : cross} alt="" />
          </div>
          {hasSentSms ? (
            <>
              <h4 className="primary-color">まだ予約は完了していません。</h4>
              <p>
                ご入力いただいた電話番号に
                <br />
                確認のメッセージを送信しました。
                <br />
                ご確認をお願いします。
              </p>
            </>
          ) : (
            <>
              <p>
                SMS送信に失敗しました。
                <br />
                申し訳ありませんが、最初から再度お試しください。
              </p>
              <SecondaryButton
                label="戻る"
                type="button"
                className="w-25 mt-3"
                onClick={handleClickPreviousStep}
              />
            </>
          )}
        </div>
      );
  }
};

export default FormComponent;
